import { PlusIcon } from '@/assets/icons/info-icons/plus';
import { ApproveIconWithDollarIcon } from '@/assets/icons/pricing-tab-icons/approveIconWithDollar';
import { TimeframeIcon } from '@/assets/icons/pricing-tab-icons/timeframe';
import { AddPageIcon } from '@/assets/static/icons/add-page-icon';
import { GearPageIcon } from '@/assets/static/icons/gear-page-icon';
import Button from '@/components/basicComponents/button';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { themeVar } from '@/styles/theme';
import React from 'react';
import styled from 'styled-components';
import { EditInvestorFormDevider } from '../../styles';

const zeroStateItems = [
  { icon: <AddPageIcon />, title: 'Group two ore more entities' },
  { icon: <TimeframeIcon />, title: 'Set allocation targets for the group' },
  { icon: <ApproveIconWithDollarIcon />, title: 'Allocate using  the group’s total capacity' },
  { icon: <GearPageIcon />, title: 'Select a specific entity for each subscription' }
];

interface EntityGroupingZeroStateProps {
  canCreateGroup: boolean;
  openEditGroupModalPage: () => void;
}

export const EntityGroupingZeroState = ({ canCreateGroup, openEditGroupModalPage }: EntityGroupingZeroStateProps) => {
  return (
    <ZeroStateWrapper>
      <ZeroStateTitle>
        <img src="/static/investors-page/missing-mag-glass-graphic.png" draggable="false" alt="No Investors" />
        <div>
          <h2>No Groups Added.</h2>
          <p>
            By default each entity within a household is managed as a separate portfolio. Group two or more entities to treat them as a single
            portfolio for managing capacity.
          </p>
        </div>
      </ZeroStateTitle>
      <EditInvestorFormDevider />
      <ZeroStateSteps>
        {zeroStateItems.map((item, index) => (
          <React.Fragment key={index}>
            <ZeroStateStepItem>
              {item.icon}
              <p>{item.title}</p>
            </ZeroStateStepItem>
            {index !== zeroStateItems.length - 1 && (
              <ZeroStateStepSeparator>
                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="21" viewBox="0 0 14 21" fill="none">
                    <path d="M0.856689 2.85001L8.48669 10.5L0.856689 18.15L3.20669 20.5L13.2067 10.5L3.20669 0.5L0.856689 2.85001Z" fill="#ABABAB" />
                  </svg>
                </div>
              </ZeroStateStepSeparator>
            )}
          </React.Fragment>
        ))}
      </ZeroStateSteps>
      <AddButtonWrapper>
        <Tooltip
          maxContentWidth={500}
          title={
            canCreateGroup
              ? ''
              : 'Groups require two or more entities. Either add additional entities to this household or ungroup entities before adding a new group.'
          }
        >
          <Button
            size="lg"
            onClick={openEditGroupModalPage}
            styleType={canCreateGroup ? 'default' : 'disabled'}
            icon={<PlusIcon width={20} height={20} fill={themeVar('context.light')} />}
          >
            Add a Group
          </Button>
        </Tooltip>
      </AddButtonWrapper>
    </ZeroStateWrapper>
  );
};

const ZeroStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  max-width: 1100px;
  width: 100%;
  margin: 100px auto;
`;

const ZeroStateTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  font-size: 20px;
  padding: 20px 80px;
  border-radius: 20px;
  color: ${themeVar('font.weak')};
  background: ${themeVar('layer.1')};
`;

const ZeroStateSteps = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const ZeroStateStepItem = styled.div`
  display: flex;
  width: 200px;
  height: 139px;
  padding: 10px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 15px;
  color: ${themeVar('font.base')};
  text-align: center;
`;

const ZeroStateStepSeparator = styled.div`
  display: flex;
  width: 40px;
  height: 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${themeVar('font.weak')};
  & > div {
    width: 20px;
    height: 12.35px;
    flex-shrink: 0;
  }
`;

const AddButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding-top: 40px;
`;
